import { Icon, Icons } from 'components/Icon';
import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TextField from 'components/Form/TextField';
import TextAreaFieldWithAutoHeightResize from 'components/Form/TextAreaFieldWithAutoHeightResize';
import Button from 'components/Form/Button';
import { useAssignBankBeneficiary } from 'common/queries-and-mutations/bank';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import { InfoModal } from 'components/Modal';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
  assetName: string;
  beneficiaryName?: string;
  bankId: number;
  beneficiaryId?: number | null;
  remainingAlloted: number;
};

export default function BankAssignAssetModal({
  onClose,
  onSuccess,
  assetName,
  beneficiaryName,
  bankId,
  beneficiaryId,
  remainingAlloted
}: Readonly<Props>) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate, isSuccess, isError, isLoading } = useAssignBankBeneficiary();

  const formik = useFormik({
    initialValues: {
      bankId: [bankId],
      beneficiary: beneficiaryId,
      allottedPercentage: '',
      note: ''
    },
    validationSchema: Yup.object({
      allottedPercentage: Yup.string().required('Please enter proportion')
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onSubmit(values);
    }
  });

  const onSubmit = (formValue: any) => {
    const payload = {
      bankId: formValue.bankId,
      beneficiaryId: formValue.beneficiary,
      allottedPercentage: Number(formValue.allottedPercentage),
      note: formValue.note
    };
    mutate(payload);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      onSuccess();
      toast(
        <Alert message="Beneficiary Successfully Assigned" type="success" />
      );
      setIsSubmitting(false);
      formik.resetForm();
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  return (
    <InfoModal width="w-[60%]" className="h-fit rounded-md">
      <div className="flex flex-row items-center justify-between p-3 pl-6">
        <p className="text-2xl font-bold text-[#353535]">
          Add <span className="text-[#FF6606]">{beneficiaryName}</span> as
          beneficiary
        </p>
        <button type="button" onClick={onClose}>
          <Icon name={Icons.Close} />
        </button>
      </div>
      <div className="mt-2 border-b" />

      <form
        autoComplete="false"
        className="px-4 pb-4 sm:p-6 sm:pb-4"
        onSubmit={formik.handleSubmit}
      >
        <div className="mb-1 mt-8">
          <div className="grid md:grid-cols-2 md:gap-10">
            <div className="group relative z-0 mb-6 w-full">
              <TextField
                name={`asset`}
                placeholder=""
                value={assetName}
                disabled
                label="Asset"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                size="md"
                className="rounded-xl border-[1.5px] border-gray-300"
              />
            </div>
            <div className="group relative z-0 mb-6 w-full">
              <div className="mb-2.5 flex items-center gap-4">
                <span className="text-xs font-medium text-[#34475E]">
                  *Allocation percent (%)
                </span>
                <span className="text-xs font-medium text-[#00B388]">
                  *{remainingAlloted}% remaining of this asset
                </span>
              </div>
              <TextField
                name="allottedPercentage"
                placeholder="Proportion (%)"
                value={formik.values.allottedPercentage}
                onChange={e => {
                  const inputValue = e.target.value;
                  if (
                    inputValue === '' ||
                    (parseInt(inputValue) >= 0 && !inputValue.includes('-'))
                  ) {
                    formik.handleChange(e);
                  }
                }}
                onBlur={e => {
                  const inputValue = e.target.value;
                  if (
                    inputValue === '' ||
                    (parseInt(inputValue) >= 0 && !inputValue.includes('-'))
                  ) {
                    formik.handleBlur(e);
                  }
                }}
                size="md"
                className="rounded-xl border-[1.5px] border-gray-300"
                error={
                  formik.touched.allottedPercentage
                    ? formik.errors.allottedPercentage
                    : ''
                }
              />
            </div>
          </div>
          <div className="grid border-t">
            <div className="group relative z-0 mb-6 mt-5 w-full">
              <TextAreaFieldWithAutoHeightResize
                label="Note"
                name={`note`}
                value={formik.values.note}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder=""
                rows={4}
                error={formik.touched.note ? formik.errors.note : ''}
                className="w-full rounded-2xl border-[1.5px] border-gray-300"
              />
            </div>
          </div>
          <div className="flex items-center justify-end">
            <Button
              type="submit"
              variant="outline"
              size="custom"
              className="place-self-end rounded bg-[#FF7A00] px-8 py-1.5 text-center text-xs font-medium text-[#FFFFFF]"
              label="Assign"
              disabled={!formik.isValid}
              loading={isSubmitting || isLoading}
            ></Button>
          </div>
        </div>
      </form>
    </InfoModal>
  );
}
